'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { getFlag, type ApiStanding } from '@/lib/api-football';
import { Trophy, RefreshCw, TrendingUp } from 'lucide-react';
import Link from 'next/link';
import { useLanguage } from '@/contexts/LanguageContext';

interface GroupData {
  name: string;
  rows: ApiStanding[];
}

function GroupTable({ group, favoriteIds }: { group: GroupData; favoriteIds: Set<number> }) {
  const { t } = useLanguage();
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
      <div className="px-4 py-3 flex items-center gap-2" style={{ background: 'hsl(222 35% 13%)', borderBottom: '1px solid hsl(222 25% 16%)' }}>
        <Trophy size={14} className="text-orange-400" />
        <span className="text-sm font-bold text-white">{group.name}</span>
        <span className="ml-auto text-xs text-slate-500">{t.standings.played} {t.standings.won} {t.standings.drawn} {t.standings.lost} {t.standings.goalsFor} {t.standings.goalsAgainst} {t.standings.goalDiff} {t.standings.points}</span>
      </div>

      <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-1 px-4 py-2 text-xs font-medium text-slate-600" style={{ borderBottom: '1px solid hsl(222 25% 14%)' }}>
        <span>{t.standings.team}</span>
        <span className="text-center">{t.standings.played}</span>
        <span className="text-center">{t.standings.won}</span>
        <span className="text-center">{t.standings.drawn}</span>
        <span className="text-center">{t.standings.lost}</span>
        <span className="text-center">{t.standings.goalsFor}</span>
        <span className="text-center">{t.standings.goalsAgainst}</span>
        <span className="text-center">{t.standings.goalDiff}</span>
        <span className="text-center font-bold text-slate-400">{t.standings.points}</span>
      </div>

      {group.rows.map((row, idx) => {
        const isQualified = idx < 2;
        const isFav = favoriteIds.has(row.team.id);
        return (
          <div
            key={row.team.id}
            className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-1 px-4 py-2.5 items-center transition-colors hover:bg-white/3"
            style={{
              borderBottom: idx < group.rows.length - 1 ? '1px solid hsl(222 25% 13%)' : 'none',
              background: isFav ? 'hsl(25 95% 53% / 0.05)' : 'transparent',
            }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{
                  background: isQualified ? 'hsl(189 94% 51% / 0.15)' : 'hsl(222 25% 18%)',
                  color: isQualified ? 'hsl(189 94% 65%)' : 'hsl(222 15% 50%)',
                }}
              >
                {row.rank}
              </span>
              <Link href={`/team/${row.team.id}`} className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity">
                {row.team.logo ? (
                  <img src={row.team.logo} alt={row.team.name} className="w-5 h-5 object-contain flex-shrink-0" />
                ) : (
                  <span className="text-base flex-shrink-0">{getFlag(row.team.name)}</span>
                )}
                <span className="text-sm font-medium text-white truncate hover:text-orange-400 transition-colors">{row.team.name}</span>
              </Link>
              {isFav && <span className="text-orange-400 text-xs flex-shrink-0">★</span>}
            </div>
            <span className="text-center text-sm text-slate-400">{row.all.played}</span>
            <span className="text-center text-sm text-slate-400">{row.all.win}</span>
            <span className="text-center text-sm text-slate-400">{row.all.draw}</span>
            <span className="text-center text-sm text-slate-400">{row.all.lose}</span>
            <span className="text-center text-sm text-slate-400">{row.all.goals.for}</span>
            <span className="text-center text-sm text-slate-400">{row.all.goals.against}</span>
            <span className={`text-center text-sm font-medium ${row.goalsDiff > 0 ? 'text-green-400' : row.goalsDiff < 0 ? 'text-red-400' : 'text-slate-400'}`}>
              {row.goalsDiff > 0 ? '+' : ''}{row.goalsDiff}
            </span>
            <span className="text-center text-sm font-bold text-white">{row.points}</span>
          </div>
        );
      })}

      <div className="px-4 py-2 flex items-center gap-3" style={{ borderTop: '1px solid hsl(222 25% 14%)', background: 'hsl(222 35% 10%)' }}>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'hsl(189 94% 51% / 0.5)' }} />
          <span className="text-xs text-slate-500">{t.standings.qualified}</span>
        </div>
      </div>
    </div>
  );
}

export default function StandingsPage() {
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [activeGroup, setActiveGroup] = useState<string>('all');
  const [favoriteIds] = useState<Set<number>>(new Set());
  const { t } = useLanguage();

  const fetchStandings = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`/api/football?endpoint=/standings&league=1&season=2026`, { cache: 'no-store' });
      const json = await res.json();

      if (json.error) {
        setError(json.error);
        setLoading(false);
        return;
      }

      if (json.errors && typeof json.errors === 'object' && Object.keys(json.errors).length > 0) {
        const errMsg = Object.values(json.errors).join(' ');
        setError(errMsg);
        setLoading(false);
        return;
      }

      const standingsData: ApiStanding[][] = json.response?.[0]?.league?.standings ?? [];
      if (standingsData.length > 0) {
        const parsed: GroupData[] = standingsData.map((groupRows: ApiStanding[], idx: number) => ({
          name: groupRows[0]?.group ?? `Group ${String.fromCharCode(65 + idx)}`,
          rows: groupRows,
        }));
        setGroups(parsed);
      }
    } catch (err) {
      setError('Failed to connect to the football API. Please try again later.');
    }
    setLoading(false);
    setLastUpdate(new Date().toLocaleTimeString());
  }, []);

  useEffect(() => {
    fetchStandings();
    const interval = setInterval(fetchStandings, 60_000);
    return () => clearInterval(interval);
  }, [fetchStandings]);

  const groupNames = groups.map(g => g.name);
  const displayGroups = activeGroup === 'all' ? groups : groups.filter(g => g.name === activeGroup);
  const totalTeams = groups.reduce((sum, g) => sum + g.rows.length, 0);

  return (
    <AppLayout activeRoute="/standings">
      <div className="space-y-6 pb-20 lg:pb-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
              <Trophy size={22} className="text-orange-400" />
              {t.standings.title}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">{t.standings.subtitle}</p>
          </div>
          {lastUpdate && (
            <span className="flex items-center gap-1 text-xs text-slate-600">
              <RefreshCw size={10} className="text-cyan-500" />
              <span className="font-mono">{lastUpdate}</span>
            </span>
          )}
        </div>

        {/* Group Filter */}
        {groupNames.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin pb-1">
            {(['all', ...groupNames] as const).map(g => (
              <button
                key={g}
                onClick={() => setActiveGroup(g)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${activeGroup === g ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}
                style={activeGroup !== g ? { background: 'hsl(222 35% 13%)' } : {}}
              >
                {g === 'all' ? t.common.all : g}
              </button>
            ))}
          </div>
        )}

        {/* Stats Banner */}
        {!loading && groups.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: t.standings.title.split(' ')[0], value: String(groups.length), icon: '🏆' },
              { label: t.common.all, value: String(totalTeams), icon: '⚽' },
              { label: t.standings.qualified.split(' ')[0], value: String(groups.length * 2), icon: '🎯' },
            ].map(stat => (
              <div key={stat.label} className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                <span className="text-2xl">{stat.icon}</span>
                <div>
                  <div className="text-xl font-black text-white">{stat.value}</div>
                  <div className="text-xs text-slate-500">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Group Tables */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => (
              <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'hsl(222 35% 11%)', height: 280 }} />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl p-8 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(0 70% 40% / 0.4)' }}>
            <div className="text-4xl mb-3">⚠️</div>
            <p className="text-red-400 font-semibold mb-2">Unable to load standings</p>
            <p className="text-slate-400 text-sm max-w-md mx-auto">{error}</p>
            {error.toLowerCase().includes('ip') && (
              <p className="text-slate-500 text-xs mt-3 max-w-md mx-auto">
                To fix this: go to your API-Sports dashboard → My Account → Allowed IPs, and either remove all IP restrictions or add the server&apos;s IP address.
              </p>
            )}
            <button
              onClick={fetchStandings}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-2xl p-12 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
            <div className="text-4xl mb-3">🏆</div>
            <p className="text-slate-300 font-semibold">{t.standings.noStandings}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {displayGroups.map(group => (
              <GroupTable key={group.name} group={group} favoriteIds={favoriteIds} />
            ))}
          </div>
        )}

        {/* Group Leaders */}
        {!loading && groups.length > 0 && (
          <div className="rounded-2xl p-4" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={15} className="text-orange-400" />
              <span className="text-sm font-semibold text-white">{t.standings.groupLeaders}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {groups.slice(0, 4).map(group => {
                const leader = group.rows[0];
                if (!leader) return null;
                return (
                  <Link
                    key={group.name}
                    href={`/team/${leader.team.id}`}
                    className="rounded-xl p-3 text-center hover:opacity-80 transition-opacity block"
                    style={{ background: 'hsl(222 35% 14%)' }}
                  >
                    {leader.team.logo ? (
                      <img src={leader.team.logo} alt={leader.team.name} className="w-8 h-8 object-contain mx-auto mb-1" />
                    ) : (
                      <div className="text-2xl mb-1">{getFlag(leader.team.name)}</div>
                    )}
                    <div className="text-sm font-semibold text-white">{leader.team.name}</div>
                    <div className="text-xs text-slate-500">{group.name} · {leader.points} pts</div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
